/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */
import Vue from 'vue';
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { ValidationError, PMSError, HttpError } from '@/errors';

const endpoint = `${apiEndpoint}/property/photo-gallery/images`;
const fileEndpoint = `${apiEndpoint}/property/file-administration`;
const faviconEndpoint = `${apiEndpoint}/property/photo-gallery/favicon`;

const state = {
  rooms: null,
  files: [],
  images: [],
  tags: null,
  sellableUnits: null,
  otaCategories: null,
  mediaCategories: null,
  pending: false,
  error: null,
  pmsError: null,
  updating: false,
  updatingImage: null,
  updatingRoom: null,
  updatingSortImageId: null,
  currentLogoUrl: null,
  tagPending: false,
  favicon: false,
};

const getters = {
  loaded: (state) => (state.images != null && !state.pending),
  fileLoaded: (state) => (state.files != null && !state.pending),
  rooms: (state) => (state.rooms != null ? state.rooms : []),
  images: (state) => (state.images != null ? state.images : []),
  files: (state) => (state.files != null ? state.files : []),
  tags: (state) => (state.tags != null ? state.tags : []),
  tagPending: (state) => (state.tagPending),
  sellableUnits: (state) => (state.sellableUnits != null ? state.sellableUnits : []),
  otaCategories: (state) => (state.otaCategories != null ? state.otaCategories : []),
  mediaCategories: (state) => (state.mediaCategories != null ? state.mediaCategories : []),
  updatingSortImageId: (state) => (state.updatingSortImageId != null ? state.updatingSortImageId : []),
  favicon: (state) => (state.favicon),
  updating: (state) => (state.updating),
};

function clearUpdating(state) {
  state.updating = false;
  state.updatingImage = null;
  state.updatingRoom = null;
}

const mutations = {
  clearErrors() {
    // do nothing
  },
  beforeLoading(state) {
    state.pending = true;
    state.rooms = null;
    state.images = null;
  },
  data(state, {
    // eslint-disable-next-line max-len
    images = null, sellableUnits = null, tags = null, otaCategories = null, error = null, favicon = null, mediaCategories = null,
  } = {}) {
    state.pending = false;
    state.images = images;
    state.tags = tags;
    state.sellableUnits = sellableUnits;
    state.otaCategories = otaCategories;
    state.mediaCategories = mediaCategories;
    state.error = error;
    state.pmsError = error;
    state.favicon = favicon;
  },
  fileData(state, {
    files = null, tags = null, otaCategories = null, error = null, mediaCategories = null,
  } = {}) {
    state.pending = false;
    state.files = files;
    state.tags = tags;
    state.otaCategories = otaCategories;
    state.mediaCategories = mediaCategories;
    state.error = error;
    state.pmsError = error;
  },
  beforeUpdate(state, { id = null, pid = null } = {}) {
    state.updating = true;
    state.updatingImage = id;
    state.updatingRoom = pid;
  },
  beforeRequest(state) {
    state.updating = true;
  },
  afterRequest(state) {
    state.updating = false;
  },
  setUpdatingSortImageId(state, id) {
    state.updatingSortImageId = id;
  },
  clearUpdatingSortImageId(state) {
    state.updatingSortImageId = null;
  },
  updated(state) {
    clearUpdating(state);
  },
  modified(state, { type, file }) {
    const idx = state[type].findIndex(({ id }) => id === file.id);
    if (idx !== -1) {
      Vue.set(state[type], idx, JSON.parse(JSON.stringify(file)));
    }
    clearUpdating(state);
  },
  sortOrderModified(state, {
    sellableUnitId, imageIds, updatedImage, sortKey,
  }) {
    imageIds.forEach((id, index) => {
      const idx = state.images.findIndex(({ id: imageId }) => Number(imageId) === Number(id));
      if (idx !== -1) {
        const image = Number(state.images[idx].id) === Number(updatedImage.id) ? updatedImage : state.images[idx];
        if (!sellableUnitId) {
          image[sortKey] = (index + 1);
        } else {
          const idxx = image.mediaSellableUnits.findIndex((media) => media.sellableUnitId === sellableUnitId);
          image.mediaSellableUnits[idxx].sortOrder = (index + 1);
        }
        Vue.set(state.images, idx, JSON.parse(JSON.stringify(image)));
      }
    });
  },
  imageName(state, { id, name }) {
    const image = state.images.find((i) => i.id === id);
    if (image) image.display_name = name;
    clearUpdating(state);
  },
  imageRooms(state, { id, rooms }) {
    const image = state.images.find((i) => i.id === id);
    if (!image) return;
    const old = [...image.rooms];
    state.rooms.forEach((room) => {
      const { pid } = room;
      const exists = room.images.includes(id);
      if (exists && old.includes(pid) && !rooms.includes(pid)) {
        // delete old
        const idx = room.images.findIndex((i) => i === id);
        room.images.splice(idx, 1);
      }
      if (!exists && rooms.includes(pid)) {
        // add new
        room.images.push(id);
      }
    });
    image.rooms = rooms;
    clearUpdating(state);
  },
  imageCreate(state, images) {
    state.images.push(...images);
    clearUpdating(state);
  },
  fileCreate(state, files) {
    state.files.push(...files);
    clearUpdating(state);
  },
  imageDelete(state, id) {
    const idx = state.images.findIndex((i) => i.id === id);
    if (idx !== -1) {
      state.images.splice(idx, 1);
    }
    clearUpdating(state);
  },
  fileDelete(state, id) {
    const idx = state.files.findIndex((i) => i.id === id);
    if (idx !== -1) {
      state.files.splice(idx, 1);
    }
    clearUpdating(state);
  },
  roomImages(state, { pid, images }) {
    const room = state.rooms.find((r) => r.pid === pid);
    if (!room) return;
    const old = [...room.images];
    state.images.forEach((image) => {
      const { id } = image;
      const exists = image.rooms.includes(pid);
      if (exists && old.includes(id) && !images.includes(id)) {
        // delete old
        const idx = image.rooms.findIndex((r) => r === pid);
        image.rooms.splice(idx, 1);
      }
      if (!exists && images.includes(id)) {
        image.rooms.push(pid);
      }
    });
    room.images = images;
    clearUpdating(state);
  },
  setCurrentLogoUrl(state, data) {
    if (data) {
      state.currentLogoUrl = data.url;
    }
  },
  beforeAddTagRequest(state) {
    state.tagPending = true;
  },
  afterAddTagRequest(state, tag) {
    if (tag) {
      state.tags.push({
        id: tag.id,
        text: tag.text,
        propertyId: tag.propertyId,
        parentId: tag.parentId,
      });
    }
    state.tagPending = false;
  },
  setFavicon(state, favicon) {
    state.updating = false;
    state.favicon = favicon;
    clearUpdating(state);
  },
  clearUpdatingState(state) {
    clearUpdating(state);
  },
};

function handleApiError(commit, error) {
  if (!error.response?.status || !error.response?.data) {
    throw error;
  }
  const { status, data } = error.response;
  switch (status) {
    case 400:
      throw new HttpError(status, data.message);
    case 409: // PMSError
      throw new PMSError(status, data.message);
    case 422:
      throw new ValidationError(status, data.message, data.errors);
    default:
      throw error;
  }
}

const actions = {
  // eslint-disable-next-line consistent-return
  async addNewTag({ commit }, payload) {
    try {
      commit('beforeAddTagRequest');
      const { data } = await ApiService.post(`${endpoint}/predefined-tag`, payload);
      commit('afterAddTagRequest', data);
      return data;
    } catch (error) {
      commit('afterAddTagRequest', null);
      handleApiError(commit, error);
    }
  },
  async fetchData({ commit }) {
    commit('beforeLoading');
    try {
      const { data } = await ApiService.get(`${endpoint}`);
      commit('data', data);
    } catch (error) {
      commit('data', { error });
    }
  },
  async fetchFileData({ commit }) {
    commit('beforeLoading');
    try {
      const { data } = await ApiService.get(`${fileEndpoint}`);
      commit('fileData', data);
    } catch (error) {
      commit('fileData', { error });
    }
  },
  async uploadImage({ commit }, {
    files, isLogo, sellableUnits, room = null, progress = null, uploadedBy = null, isMain = false, returnUrl = false,
  }) {
    commit('beforeUpdate');
    try {
      const formData = new FormData();
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < files.length; i++) {
        formData.append('images[]', files[i], files[i].name);
      }
      formData.append('isLogo', isLogo);
      formData.append('isMain', isMain);
      sellableUnits.forEach((sellableUnit) => {
        formData.append('sellableUnits[]', sellableUnit);
      });
      if (room != null) {
        formData.append('room', room);
      }
      if (uploadedBy) {
        formData.append('uploadedBy', uploadedBy);
      }

      const config = {};
      if (progress) {
        config.onUploadProgress = progress;
      }
      // if (complete) {
      //   config.onUpload = complete;
      // }
      const { data: images } = await ApiService.post(`${endpoint}`, formData, config);
      if (!returnUrl) {
        commit('imageCreate', images);
      } else {
        commit('clearUpdatingState');
      }
      return images;
    } catch (error) {
      commit('updated');
      throw handleApiError(commit, error);
    }
  },
  async uploadFavicon({ commit }, { files, progress = null }) {
    commit('beforeUpdate');
    try {
      const formData = new FormData();
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < files.length; i++) {
        formData.append('favicon', files[i], files[i].name);
      }
      const config = {};
      if (progress) {
        config.onUploadProgress = progress;
      }
      const { data: item } = await ApiService.post(`${faviconEndpoint}`, formData, config);
      commit('setFavicon', item?.favicon);
      return item;
    } catch (error) {
      commit('updated');
      throw handleApiError(commit, error);
    }
  },
  async updateImage({ commit }, payload) {
    const { id } = payload;
    commit('beforeUpdate', { id });
    try {
      const { data } = await ApiService.put(`${endpoint}/${id}`, payload);
      commit('modified', { type: 'images', file: data });
    } catch (error) {
      commit('updated');
      throw handleApiError(commit, error);
    }
  },
  async updateSortOrder({ commit }, payload) {
    try {
      await ApiService.put(`${endpoint}/update-sort-order`, payload);
      commit('updated');
    } catch (error) {
      commit('updated');
    }
  },
  // eslint-disable-next-line consistent-return
  async updateSellableUnits({ commit }, payload) {
    const { id } = payload;
    try {
      const { data } = await ApiService.put(`${endpoint}/${id}/update-sellable-units`, payload);
      commit('updated');
      return data;
    } catch (error) {
      commit('updated');
    }
  },
  async updateName({ commit }, { id, name }) {
    const obj = state.images.find((i) => i.id === id);
    if (obj == null) return;
    const original = obj.name;
    commit('imageName', { id, name });
    commit('beforeUpdate', { id });
    try {
      await ApiService.put(`${endpoint}/${id}`, { name });
      commit('updated');
    } catch (error) {
      commit('imageName', { id, name: original });
    }
  },
  async updateRooms({ commit }, { id, rooms }) {
    const obj = state.images.find((i) => i.id === id);
    if (obj == null) return;
    const original = [...obj.rooms];
    commit('imageRooms', { id, rooms });
    commit('beforeUpdate', { id });
    try {
      await ApiService.put(`${endpoint}/${id}`, { rooms });
      commit('updated');
    } catch (error) {
      commit('imageRooms', { id, rooms: original });
    }
  },
  async reorderImages({ commit }, { pid, images }) {
    const room = state.rooms.find((r) => r.pid === pid);
    if (room == null) return;
    const original = [...room.images];
    commit('roomImages', { pid, images });
    commit('beforeUpdate', { pid });
    try {
      await ApiService.patch(`${endpoint}/${pid}`, { images });
      commit('updated');
    } catch (error) {
      commit('roomImages', { pid, images: original });
    }
  },
  async deleteImage({ commit }, id) {
    commit('beforeUpdate', { id });
    try {
      await ApiService.delete(`${endpoint}/${id}`);
      commit('imageDelete', id);
    } catch (error) {
      commit('updated');
      throw handleApiError(commit, error);
    }
  },
  async deleteFavicon({ commit }) {
    commit('beforeRequest');
    try {
      await ApiService.delete(`${faviconEndpoint}`);
      commit('setFavicon', null);
      commit('afterRequest');
    } catch (error) {
      commit('updated');
      throw handleApiError(commit, error);
    }
  },
  async uploadImageRoomDB({ commit }, {
    file, room = null, progress = null, returnUrl = false,
  }) {
    commit('beforeUpdate');
    try {
      const formData = new FormData();
      formData.append('image', file, file.name);
      if (room != null) {
        formData.append('room', room);
      }
      const config = {};
      if (progress) {
        config.onUploadProgress = progress;
      }
      // if (complete) {
      //   config.onUpload = complete;
      // }
      const { data: image } = await ApiService.post(`${endpoint}/upload/roomdb`, formData, config);
      if (returnUrl) {
        return image.url;
      }
      // commit('imageCreate', image);
      return image;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return '';
    }
  },
  async getCurrentLogo({ commit }) {
    try {
      const { data } = await ApiService.get(`${endpoint}/current-logo`);
      commit('setCurrentLogoUrl', data);
    } catch (error) {
      throw handleApiError(commit, error);
    }
  },
  async updateFile({ commit }, payload) {
    const { id } = payload;
    commit('beforeUpdate', { id });
    try {
      const { data } = await ApiService.put(`${fileEndpoint}/${id}`, payload);
      commit('modified', { type: 'files', file: data });
    } catch (error) {
      commit('updated');
      throw handleApiError(commit, error);
    }
  },
  async uploadFile({ commit }, {
    files, progress = null, uploadedBy = null, isMain = false, mediaCategory = null,
  }) {
    commit('beforeUpdate');
    try {
      const formData = new FormData();
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < files.length; i++) {
        formData.append('files[]', files[i], files[i].name);
      }
      formData.append('isMain', isMain);
      if (mediaCategory) {
        formData.append('mediaCategoryId', mediaCategory);
      }
      if (uploadedBy) {
        formData.append('uploadedBy', uploadedBy);
      }

      const config = {};
      if (progress) {
        config.onUploadProgress = progress;
      }
      const { data: media } = await ApiService.post(`${fileEndpoint}`, formData, config);
      commit('fileCreate', media);
      return media;
    } catch (error) {
      commit('updated');
      throw handleApiError(commit, error);
    }
  },
  async deleteFile({ commit }, id) {
    commit('beforeUpdate', { id });
    try {
      await ApiService.delete(`${fileEndpoint}/${id}`);
      commit('fileDelete', id);
    } catch (error) {
      commit('updated');
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
